import { useEffect, useRef, useState } from "react";
import { Play, Pause, X, Loader2, Volume2, Download } from "lucide-react";
import { api } from "@/lib/api";

export function VideoPresentation({ endpoint, variant = "full" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [frac, setFrac] = useState(0);
  const [segIdx, setSegIdx] = useState(0);
  const audioRef = useRef(null);
  const boundsRef = useRef([]);

  const start = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data: d } = await api.get(`${endpoint}/presentation`);
      setData(d);
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${d.audio_url}`);
      audioRef.current = audio;
      const weights = d.segments.map((s) => s.text.length);
      const total = weights.reduce((a, b) => a + b, 0);
      const bounds = [];
      let acc = 0;
      weights.forEach((w) => { acc += w / total; bounds.push(acc); });
      boundsRef.current = bounds;
      audio.ontimeupdate = () => {
        if (!audio.duration) return;
        const f = audio.currentTime / audio.duration;
        setFrac(f);
        const idx = bounds.findIndex((b) => f < b);
        setSegIdx(Math.min(idx === -1 ? bounds.length - 1 : idx, d.segments.length - 1));
      };
      audio.onended = () => { setPlaying(false); setFrac(1); };
      audio.oncanplaythrough = () => setLoading(false);
      await audio.play();
      setPlaying(true);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  const close = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setOpen(false); setPlaying(false); setFrac(0); setSegIdx(0); setData(null);
  };

  useEffect(() => () => audioRef.current?.pause(), []);

  const progress = frac * 100;
  const karaokeWords = () => {
    const seg = data?.segments?.[segIdx];
    if (!seg) return null;
    const bounds = boundsRef.current;
    const segStart = segIdx === 0 ? 0 : bounds[segIdx - 1];
    const segEnd = bounds[segIdx] ?? 1;
    const within = segEnd > segStart ? Math.min(Math.max((frac - segStart) / (segEnd - segStart), 0), 1) : 1;
    const words = seg.text.split(" ");
    const revealed = Math.ceil(within * words.length);
    return words.map((w, i) => (
      <span key={i} className="transition-colors duration-200"
        style={{ color: i < revealed ? "var(--bone)" : "rgba(245,245,240,0.35)", fontWeight: i === revealed - 1 ? 700 : 400 }}>
        {w}{" "}
      </span>
    ));
  };

  return (
    <>
      {variant === "icon" ? (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); start(); }}
          className="flex items-center justify-center w-9 h-9 rounded-full transition-transform hover:scale-110 shrink-0"
          style={{ background: "var(--orange)", color: "var(--bone)" }}
          aria-label="Voir la présentation vidéo" data-testid="video-presentation-icon-btn">
          <Play size={14} fill="currentColor" style={{ marginLeft: 2 }} />
        </button>
      ) : (
        <button onClick={start} className="btn-primary mt-5 px-6 py-3.5 font-semibold inline-flex items-center gap-3" data-testid="video-presentation-btn">
          <span className="flex items-center justify-center w-8 h-8 rounded-full" style={{ background: "rgba(245,245,240,0.25)" }}><Play size={15} fill="currentColor" /></span>
          Voir la présentation vidéo
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" style={{ background: "rgba(10,10,9,0.88)", backdropFilter: "blur(8px)" }} data-testid="video-presentation-overlay">
          <div className="w-full max-w-4xl relative overflow-hidden" style={{ background: "var(--ink)", border: "1px solid rgba(245,245,240,0.2)" }}>
            <button onClick={close} className="absolute top-4 right-4 z-20 p-2 rounded-full" style={{ background: "rgba(20,20,18,0.7)", color: "var(--bone)" }} data-testid="video-presentation-close">
              <X size={20} />
            </button>

            <div className="relative aspect-video overflow-hidden">
              {data?.image && (
                <img src={data.image} alt={data.title} className="absolute inset-0 w-full h-full object-cover"
                  style={{ animation: playing ? "kenburns 24s ease-in-out infinite alternate" : "none", transform: "scale(1.05)" }} />
              )}
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,9,0.92) 0%, rgba(10,10,9,0.35) 45%, rgba(10,10,9,0.25) 100%)" }} />

              <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5" style={{ background: "rgba(245,245,240,0.92)", borderRadius: 4 }} data-testid="video-presentation-logo">
                <img src="/aldol-logo.png" alt="ALDOL-SOLUTIONS" className="h-9 w-9 object-contain" />
                <span className="font-display font-black text-xs tracking-wide" style={{ color: "var(--ink)" }}>ALDOL-SOLUTIONS</span>
              </div>

              {loading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" style={{ color: "var(--bone)" }} data-testid="video-presentation-loading">
                  <Loader2 size={40} className="animate-spin" style={{ color: "var(--orange)" }} />
                  <p className="text-sm font-semibold">Préparation de la présentation…</p>
                </div>
              ) : (
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-10" style={{ color: "var(--bone)" }}>
                  <p className="overline flex items-center gap-2" style={{ color: "var(--orange)" }}>
                    <Volume2 size={13} /> {data?.domain} · {data?.segments?.[segIdx]?.label}
                  </p>
                  <h3 className="font-display font-black text-xl md:text-3xl mt-2 leading-tight">{data?.title}</h3>
                  <p key={segIdx} className="mt-4 text-sm md:text-lg max-w-2xl" data-testid="video-presentation-caption"
                    style={{ animation: "fadeUp 0.6s ease both" }}>
                    {karaokeWords()}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 px-6 py-4" style={{ borderTop: "1px solid rgba(245,245,240,0.15)" }}>
              <button onClick={toggle} disabled={loading} className="flex items-center justify-center w-11 h-11 rounded-full shrink-0"
                style={{ background: "var(--orange)", color: "var(--bone)" }} data-testid="video-presentation-toggle">
                {playing ? <Pause size={18} /> : <Play size={18} fill="currentColor" style={{ marginLeft: 2 }} />}
              </button>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(245,245,240,0.2)" }}>
                <div className="h-full transition-[width] duration-300" style={{ width: `${progress}%`, background: "var(--orange)" }} data-testid="video-presentation-progress" />
              </div>
              {data && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`} download className="p-2 rounded-full shrink-0 transition-opacity hover:opacity-70"
                  style={{ border: "1px solid rgba(245,245,240,0.3)", color: "var(--bone)" }} aria-label="Télécharger le commentaire MP3" data-testid="video-presentation-download">
                  <Download size={15} />
                </a>
              )}
              <span className="text-[10px] uppercase tracking-wider hidden sm:block" style={{ color: "rgba(245,245,240,0.5)" }}>Commentaire généré par IA</span>
            </div>
          </div>
          <style>{`
            @keyframes kenburns { 0% { transform: scale(1.05) translate(0,0); } 100% { transform: scale(1.22) translate(-2.5%, -2%); } }
            @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>
        </div>
      )}
    </>
  );
}
